/**
 * AI agent changes waiting for a person (migrations 070, 074, 096). Since 096
 * an agent's change applies at once, with an Undo (the dashboard banner,
 * ./AgentChangesBanner.tsx, and the owners' email); only a HELD change waits
 * here — inviting a teammate — plus any change from before 096 still inside
 * its old window (apply it now or reject it) or from before 074 (approve or
 * reject). Applying runs the same server code as doing it by hand; a new
 * webhook's signing secret is shown here, once, to the person who applies it
 * — the agent never sees it.
 *
 * The agent's note is its own words and is shown as a quote.
 *
 * `usePendingChanges` + `PendingList` are the pieces AI agent access uses for
 * its top-of-page callout and its list under Connected agents. `CONFIRM` is
 * shared with the dashboard banner.
 */
import { useState, type ReactNode } from 'react';
import type { PendingChange } from '../api/types';
import { useSession } from '../auth/session';
import { SecretReveal } from '../screens/Destinations';
import { ErrorNotice, Modal, Time, useToast } from '../ui/kit';
import { useMutation, useQuery, type QueryState } from '../ui/hooks';

type PendingQuery = QueryState<{ items: PendingChange[] }>;

/** What the confirm dialog says per kind. */
export const CONFIRM: Record<PendingChange['kind'], { title: string; body: string }> = {
  'destination.create': {
    title: 'Add this lead destination?',
    body: 'It will receive the homeowner’s contact details and diagnosis for every lead. Check that you recognise the address and that it belongs to your organisation.',
  },
  'destination.update': {
    title: 'Point this destination at the new address?',
    body: 'The new address will receive the homeowner’s contact details and diagnosis for every lead. Check that you recognise it and that it belongs to your organisation.',
  },
  'vendor.activate': {
    title: 'Switch these contractors on?',
    body: 'Routing can then offer them leads with the homeowner’s contact details. The agent relayed your organisation’s confirmation that it has bound them, in writing, to the FinderlyFix contractor contact rules.',
  },
  'routing.escalation': {
    title: 'Use these escalation contacts?',
    body: 'They will be copied on lead notices, which include the homeowner’s details. Check that you recognise every address.',
  },
  'member.invite': {
    title: 'Invite this person to your team?',
    body: 'They are emailed an invitation saying an AI agent invited them on your organisation’s behalf, and join with the role named in the summary only if they accept it within 7 days. Check that you recognise the address. You can revoke the invitation on Team until they accept; only a person can change their role or remove them later.',
  },
  'vendor.reconsent': {
    title: 'Lift this contractor’s opt-out?',
    body: 'They will be offered jobs again. The agent recorded that the contractor themselves told you they want offers again; check the date, channel and evidence in the summary. If they did not ask, reject it.',
  },
  'config.contact_targets': {
    title: 'Publish these widget settings now?',
    body: 'They send homeowners to the new phone number or link in the summary: the result screen’s call or booking button, the emergency number or the link back to your site. Check that you recognise every one and that it belongs to your organisation.',
  },
  'site.network_fallback': {
    title: 'Turn the network fallback on?',
    body: 'Requests nobody on your team can take will be offered to the contractor network named in the summary, and homeowners who tick its consent box are passed to it. You can turn it off at any time in Settings.',
  },
};

export function usePendingChanges(enabled = true): PendingQuery & { pending: PendingChange[] } {
  const { api } = useSession();
  const list = useQuery(() => api.listPendingChanges(), [], { enabled });
  return { ...list, pending: (list.data?.items ?? []).filter((c) => c.status === 'pending') };
}

/**
 * The pending changes with Reject / Review and approve, the confirm dialog and
 * the one-time secret. `frame` wraps the list and is rendered only while
 * something waits (or failed to load); the dialog and the secret stay mounted,
 * so approving the last change still shows its signing secret.
 */
export function PendingList({
  list,
  onChanged,
  frame = (items) => items,
}: {
  list: ReturnType<typeof usePendingChanges>;
  onChanged?: () => void;
  frame?: (items: ReactNode) => ReactNode;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [confirming, setConfirming] = useState<PendingChange | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const approve = useMutation(async (id: string) => {
    const res = await api.approvePendingChange(id);
    setConfirming(null);
    if (res.secret) setSecret(res.secret);
    else toast('Applied');
    void list.reload();
    onChanged?.();
    return res;
  });
  const reject = useMutation(async (id: string) => {
    await api.rejectPendingChange(id);
    toast('Rejected');
    void list.reload();
    return null;
  });

  const error = list.error ?? approve.error ?? reject.error;
  const items = (
    <div className="stack">
      <ErrorNotice error={error} />
      {list.pending.map((c) => (
        <article key={c.id} className="stack-sm pending-item">
          <div className="row-between" style={{ alignItems: 'flex-start' }}>
            <div className="stack-sm" style={{ gap: 2 }}>
              <strong style={{ color: 'var(--ink)', overflowWrap: 'anywhere' }}>{c.summary}</strong>
              <span className="small muted">
                Asked for by agent token “{c.requested_by.agent_token_label ?? 'unknown'}” ({c.requested_by.email ?? 'unknown'}) ·{' '}
                <Time iso={c.created_at} /> ·{' '}
                {c.applies_at ? (
                  <>
                    takes effect <Time iso={c.applies_at} /> unless you reject it
                  </>
                ) : (
                  <>
                    expires <Time iso={c.expires_at} />
                  </>
                )}
              </span>
            </div>
            <div className="row">
              <button type="button" className="btn btn-sm" disabled={reject.pending} onClick={() => void reject.run(c.id)}>
                Reject
              </button>
              <button type="button" className="btn btn-sm btn-primary" onClick={() => setConfirming(c)}>
                {c.applies_at ? 'Apply now' : 'Review and approve'}
              </button>
            </div>
          </div>
          {c.note ? (
            <p className="small agent-quote">
              <span className="visually-hidden">The agent’s note: </span>“{c.note}”
            </p>
          ) : null}
        </article>
      ))}
    </div>
  );

  return (
    <>
      {list.pending.length || error ? frame(items) : null}
      <Modal
        open={Boolean(confirming)}
        title={confirming ? CONFIRM[confirming.kind].title : ''}
        onClose={() => setConfirming(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setConfirming(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={approve.pending} onClick={() => confirming && void approve.run(confirming.id)}>
              {approve.pending ? 'Applying…' : confirming?.applies_at ? 'Apply now' : 'Approve'}
            </button>
          </>
        }
      >
        {confirming ? (
          <div className="stack-sm">
            <p>
              <strong style={{ overflowWrap: 'anywhere' }}>{confirming.summary}</strong>
            </p>
            <p>{CONFIRM[confirming.kind].body}</p>
            {confirming.kind === 'destination.create' && confirming.payload.kind === 'webhook' ? (
              <p className="small muted">You’ll see its signing secret once, after applying.</p>
            ) : null}
          </div>
        ) : null}
      </Modal>
      <SecretReveal secret={secret} onDone={() => setSecret(null)} />
    </>
  );
}
